import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  MessageSquare, Search, Send, RefreshCw, Inbox, CheckCircle2, Circle,
  Filter, X, Plus, Trash2, Pencil, FileText, ExternalLink, ChevronRight,
  Paperclip, Download, ChevronLeft, Shield, ArrowLeft,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_OPTS = [
  { value: "all", label: "Toate platformele" },
  { value: "emag_ro_fbe", label: "eMAG RO FBE" },
  { value: "emag_ro_bau", label: "eMAG RO BAU" },
  { value: "emag_bg_fbe", label: "eMAG BG FBE" },
  { value: "emag_bg_bau", label: "eMAG BG BAU" },
  { value: "emag_hu_fbe", label: "eMAG HU FBE" },
  { value: "emag_hu_bau", label: "eMAG HU BAU" },
];

const STATUS_OPTS = [
  { value: "all", label: "Toate" },
  { value: "unresolved", label: "Nerezolvate" },
  { value: "resolved", label: "Rezolvate" },
];

function decodeEntities(s) {
  if (!s || !s.includes("&")) return s;
  const t = document.createElement("textarea");
  t.innerHTML = s;
  return t.value;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
}

function groupByOrder(items) {
  const map = new Map();
  for (const m of items) {
    // Fallback key for cases WITHOUT an order — use the composite id but
    // keep it clean. The frontend detects the colon later to route to the
    // `/case/{id}` endpoint instead of `/order/{id}`.
    const k = m.order_id || m._composite_id;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(m);
  }
  const arr = Array.from(map.entries()).map(([orderId, msgs]) => {
    msgs.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const last = msgs[msgs.length - 1];
    const unread = msgs.filter((m) => m.is_unread).length;
    // `hasOrder` = we have a real numeric eMAG order id (used to show
    // "Comanda #123" vs a case-only label like "Thread eMAG HU FBE").
    const hasOrder = last?.order_id != null;
    return { orderId, msgs, last, unread, integration: last?._integration_key,
             hasOrder, caseType: last?.case_type, caseId: last?.id,
             subject: last?.subject, subcategory: last?.subcategory,
             typeName: last?.type_name, initiatedBy: last?.initiated_by };
  });
  arr.sort((a, b) => (b.last?.created_at || "").localeCompare(a.last?.created_at || ""));
  return arr;
}

function threadDisplayLabel(g) {
  // For claims/threads tied to an eMAG order, "ORDER ID" is the recognisable
  // title clients use. For self-opened threads (brand infringement etc.)
  // eMAG stores the full "Titlu: ..." string in subject.
  // User cerinta Oct 2026: ID-ul eMAG NU mai apare in titlu — e mutat pe un
  // sub-rand separat (`threadIdSubtitle`), ca sa fie mai lizibil.
  if (g.hasOrder) return `ORDER ID: #${g.orderId}`;
  if (g.subject) return g.subject.slice(0, 90);
  const ct = (g.caseType || "thread").toUpperCase();
  return `${ct} #${g.caseId ?? "?"}`;
}

// User cerinta Oct 2026: afiseaza ID-ul eMAG sub titlu, nu la final.
// Daca nu avem caseId, intoarce string gol (nu afisa nimic).
function threadIdSubtitle(g) {
  return g.caseId ? `eMAG #${g.caseId}` : "";
}

// User cerinta Oct 2026: ascundem thread-urile fara niciun fel de informatie
// utila (fara order_id, fara subject, fara caseId) — rezultate ca „THREAD #?"
// in sidebar. Sunt orfane/noise de la eMAG care nu au ce cauta in UI.
function isUsefulGroup(g) {
  if (g.hasOrder) return true;             // comenzi reale — mereu afisate
  if (g.subject && g.subject.trim()) return true;
  if (g.caseId) return true;
  return false;
}

// eMAG userType → RO label used in the "Sursa" column
const SOURCE_LABEL = {
  customer: "Client",
  emag_operator: "Operator eMAG",
  vendor: "Noi (Bragus)",
  account_manager: "Account Manager",
  salesforce: "SF",
};

// Download an eMAG claims-messaging attachment via our proxy endpoint.
// Blob approach keeps the JWT header attached — a plain <a href> would
// fail on iOS Safari PWA (same fix as MailPage).
async function downloadCaseAttachment(parentCaseId, commentId, attachmentId, filename) {
  try {
    const url = `${API}/messages/attachment/${encodeURIComponent(parentCaseId)}/${commentId}/${attachmentId}`;
    const r = await axios.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || `attachment-${attachmentId}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 15000);
  } catch (e) {
    toast.error("Eroare descărcare atașament: " + (e.response?.data?.detail || e.message));
  }
}

export default function Messages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [caseType, setCaseType] = useState("all");
  const [q, setQ] = useState("");
  const [messageIdFilter, setMessageIdFilter] = useState("");
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null); // order_id
  const [mobileOpen, setMobileOpen] = useState(false); // phone: conversation full-screen
  const openThread = (oid) => { setSelectedOrder(oid); setMobileOpen(true); };

  // Deep-link filters: `/messages?platform=emag_ro_fbe&order_id=436139690`
  // fired by Dashboard widgets. Only apply once on mount.
  const [sp] = useSearchParams();
  useEffect(() => {
    const p = sp.get("platform");
    const oid = sp.get("order_id");
    if (p) setPlatform(p);
    if (oid) { setOrderIdFilter(oid); setSelectedOrder(oid); setMobileOpen(true); }
  }, []);
  const [replyText, setReplyText] = useState("");
  const [warrantyAsk, setWarrantyAsk] = useState(false);
  const [replying, setReplying] = useState(false);
  // Synchronous guard against double-clicks — React's `setReplying(true)` is
  // async so a rapid second tap can slip through before `disabled` applies.
  const replyInFlightRef = useRef(false);
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [tplEdit, setTplEdit] = useState(null); // {id?, name, body}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, per_page: 200 };
      if (platform !== "all") params.platform = platform;
      if (status !== "all") params.status = status;
      if (caseType !== "all") params.case_type = caseType;
      if (q.trim()) params.q = q.trim();
      if (messageIdFilter.trim()) params.emag_id = messageIdFilter.trim();
      if (orderIdFilter.trim()) params.order_id = orderIdFilter.trim();
      const r = await axios.get(`${API}/messages`, { params });
      setItems(r.data?.items || []);
      // Force fresh hydration next time a thread is opened (list may
      // include new replies since last cache-hit).
      setThreadCache({});
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [platform, status, caseType, q, messageIdFilter, orderIdFilter]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/templates`);
      setTemplates(r.data?.items || []);
    } catch (_) {}
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/messages/sync`);
      toast.success(`Sincronizat: ${r.data?.fetched || 0} mesaje (${r.data?.new || 0} noi)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSyncing(false); }
  };

  const groups = useMemo(
    // User cerinta Oct 2026: elimina thread-urile goale („THREAD #?") care
    // nu au order_id, subject sau caseId. Sunt noise ingurgitat de la eMAG
    // (notificari fara continut) care polueaza sidebar-ul.
    () => groupByOrder(items).filter(isUsefulGroup),
    [items],
  );
  const [threadDetails, setThreadDetails] = useState(null); // {orderId, msgs, loading}
  // Cache of hydrated conversations keyed by orderId. Prevents re-fetching
  // the same thread every time the operator clicks back to it — user report
  // 2026-08-19: navigating between messages felt sluggish because each
  // click re-triggered the hydration API. Cache is invalidated on `load()`.
  const [threadCache, setThreadCache] = useState({}); // {orderId: msgs}

  // When a thread is opened, fetch the hydrated conversation (full comment
  // bodies) via `/messages/order/{orderId}`. The list endpoint only carries
  // per-case metadata (subject, status, dates, no body), so without this
  // extra call the chat bubbles would render as "—".
  // For threads WITHOUT an order_id (self-opened threads about brand
  // infringement / billing / account issues) we fall back to
  // `/messages/case/{composite_id}` so the hydration still works.
  useEffect(() => {
    if (!selectedOrder) { setThreadDetails(null); return; }
    let cancelled = false;
    // If we've already hydrated THIS thread in this session, hit the cache
    // immediately — no spinner, no API call (user request 2026-08-19).
    const cached = threadCache[selectedOrder];
    if (cached && cached.length > 0) {
      setThreadDetails({ orderId: selectedOrder, msgs: cached, loading: false });
      return;
    }
    (async () => {
      setThreadDetails({ orderId: selectedOrder, msgs: [], loading: true });
      try {
        // Detect a "fake" order id (composite fallback used when the case
        // has no linked order). Composite ids contain colons.
        const isComposite = String(selectedOrder).includes(":");
        const url = isComposite
          ? `${API}/messages/case/${encodeURIComponent(selectedOrder)}`
          : `${API}/messages/order/${selectedOrder}`;
        const r = await axios.get(url);
        if (cancelled) return;
        const msgs = r.data?.items || [];
        setThreadDetails({ orderId: selectedOrder, msgs, loading: false });
        setThreadCache((prev) => ({ ...prev, [selectedOrder]: msgs }));
      } catch (e) {
        if (cancelled) return;
        toast.error("Eroare încărcare mesaje: " + (e.response?.data?.detail || e.message));
        setThreadDetails({ orderId: selectedOrder, msgs: [], loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the first visible thread on initial mount so the reader
  // pane isn't empty (user report 2026-08-19: primul mesaj apare selectat
  // dar conversația e goală). Only runs when `selectedOrder` is null AND
  // groups are already loaded — never overwrites an operator's click.
  useEffect(() => {
    if (selectedOrder) return;
    if (!groups || groups.length === 0) return;
    setSelectedOrder(groups[0].orderId);
  }, [groups, selectedOrder]);

  const activeThread = useMemo(() => {
    const listGroup = selectedOrder
      ? groups.find((g) => String(g.orderId) === String(selectedOrder))
      : groups[0] || null;
    if (!listGroup) return null;
    // If we have hydrated msgs for THIS order, swap them in; otherwise fall
    // back to the metadata rows so the UI never blanks.
    if (threadDetails && String(threadDetails.orderId) === String(listGroup.orderId) && threadDetails.msgs.length > 0) {
      return { ...listGroup, msgs: threadDetails.msgs, hydrated: true, loading: threadDetails.loading };
    }
    return { ...listGroup, hydrated: false, loading: !!threadDetails?.loading };
  }, [groups, selectedOrder, threadDetails]);

  // mark all messages in active thread as read when opened
  useEffect(() => {
    if (!activeThread) return;
    const unread = activeThread.msgs.filter((m) => m.is_unread);
    if (unread.length === 0) return;
    (async () => {
      for (const m of unread) {
        try {
          await axios.post(`${API}/messages/${encodeURIComponent(m._composite_id)}/read`);
        } catch (_) {}
      }
      setItems((prev) => prev.map((it) => activeThread.msgs.some((m) => m._composite_id === it._composite_id) ? { ...it, is_unread: false } : it));
    })();
  }, [activeThread?.orderId]); // eslint-disable-line

  const [replyFiles, setReplyFiles] = useState([]);
  const [helpfulModalBusy, setHelpfulModalBusy] = useState(false);
  const [newThreadOpen, setNewThreadOpen] = useState(false);

  // Detect the eMAG helpfulness close prompt in the last operator/customer
  // comment. When present, we swap the free-text textarea for a "DA/NU"
  // radio + Submit UX (matches eMAG Seller Center UI).
  const helpfulPrompt = useMemo(() => {
    if (!activeThread) return null;
    const last = [...(activeThread.msgs || [])].reverse().find((m) => m.from_buyer);
    if (!last) return null;
    const txt = String(last.text || "").toLowerCase();
    const patterns = [
      "raspunsul primit te-a ajutat sa rezolvi",
      "răspunsul primit te-a ajutat să rezolvi",
      "did the response help you resolve",
    ];
    return patterns.some((p) => txt.includes(p)) ? last : null;
  }, [activeThread]);

  const handleReply = async (directText) => {
    const text = typeof directText === "string" ? directText : replyText;
    const files = typeof directText === "string" ? [] : replyFiles;
    if (!activeThread) {
      toast.error("Nicio conversație selectată.");
      return;
    }
    if (!text.trim() && files.length === 0) return;
    // Resolve the target message to reply to. Priority:
    //   1. Latest buyer/operator message (from_buyer=true)
    //   2. Latest message of any kind in the thread (some claim threads
    //      contain only vendor comments, e.g. brand-infringement disputes
    //      opened by us — replying re-uses our own last message id)
    //   3. `activeThread.last` (metadata fallback from `groupByOrder`)
    const msgsArr = Array.isArray(activeThread.msgs) ? activeThread.msgs : [];
    const lastBuyerMsg =
      [...msgsArr].reverse().find((m) => m?.from_buyer) ||
      msgsArr[msgsArr.length - 1] ||
      activeThread.last;
    if (!lastBuyerMsg || !lastBuyerMsg._composite_id) {
      // eslint-disable-next-line no-console
      console.error("[handleReply] Cannot resolve composite_id", { activeThread, lastBuyerMsg });
      toast.error("Nu găsesc mesajul țintă pentru răspuns. Reîmprospătează pagina (F5).");
      return;
    }
    // Bulletproof double-click guard — synchronous flag beats the async
    // `setReplying(true)` render latency that lets a rapid second tap
    // through even when the button is `disabled`.
    if (replyInFlightRef.current) return;
    replyInFlightRef.current = true;
    setReplying(true);
    try {
      let r;
      if (files.length > 0) {
        // Multipart flow — content + attachments
        const fd = new FormData();
        fd.append("content", text);
        for (const f of files) fd.append("files", f);
        r = await axios.post(
          `${API}/messages/${encodeURIComponent(lastBuyerMsg._composite_id)}/reply-multipart`,
          fd, { headers: { "Content-Type": "multipart/form-data" } },
        );
        const errs = r.data?.attachments_errors || [];
        if (errs.length) toast.warning(`Atașamente cu erori: ${errs.map((e) => e.filename).join(", ")}`);
        else toast.success(`Răspuns trimis cu ${r.data?.attachments_uploaded || 0} atașament(e).`);
      } else {
        r = await axios.post(
          `${API}/messages/${encodeURIComponent(lastBuyerMsg._composite_id)}/reply`,
          { text },
        );
        toast.success("Răspuns trimis.");
      }
      if (typeof directText !== "string") {
        setReplyText("");
        setReplyFiles([]);
      }
      // NU adăugăm optimistic mesajul in `items` — inserția locală face
      // ca după sync-ul următor (care ia răspunsul de la eMAG) să apară
      // dublat (o dată stub-ul nostru cu `_composite_id=parent:reply_UUID`,
      // o dată versiunea reală cu `parent:c{comment_id}`). Fix Feb 2026:
      // aștept sync-ul natural (2-3s) să afișeze răspunsul.
      // Reflect the backend's status transition locally so the sidebar pill
      // flips from „In asteptare" → „Raspuns" without needing a full reload.
      // We match by the parent composite id (strip any `:c<commentId>`
      // hydrated suffix from the target message).
      await load();
      // Forțează re-hydration pentru thread-ul curent — aduce versiunea
      // reală de la eMAG in DB + în UI, fără duplicate.
      if (selectedOrder) {
        try {
          const isComp = String(selectedOrder).includes(":");
          const url = isComp
            ? `${API}/messages/case/${encodeURIComponent(selectedOrder)}`
            : `${API}/messages/order/${selectedOrder}`;
          const rr = await axios.get(url);
          setThreadDetails({ orderId: selectedOrder, msgs: rr.data?.items || [], loading: false });
        } catch (_) { /* best-effort */ }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[handleReply] axios error", e);
      toast.error(e.response?.data?.detail || e.message || "Trimiterea a eșuat.");
    } finally {
      setReplying(false);
      replyInFlightRef.current = false;
    }
  };

  const handleHelpful = async (isHelpful) => {
    if (!activeThread || !helpfulPrompt) return;
    setHelpfulModalBusy(true);
    try {
      const msgsArr = Array.isArray(activeThread.msgs) ? activeThread.msgs : [];
      const lastBuyerMsg =
        [...msgsArr].reverse().find((m) => m?.from_buyer) ||
        msgsArr[msgsArr.length - 1] ||
        activeThread.last;
      if (!lastBuyerMsg || !lastBuyerMsg._composite_id) {
        toast.error("Nu găsesc mesajul țintă. Reîmprospătează pagina.");
        return;
      }
      await axios.post(
        `${API}/messages/${encodeURIComponent(lastBuyerMsg._composite_id)}/reply`,
        { text: isHelpful ? "DA" : "NU" },
      );
      if (isHelpful) {
        toast.success("Confirmat — cazul va trece în Rezolvat.");
      } else {
        toast.success("Trimis NU — cazul continuă.");
      }
      // Reload thread to fetch latest state
      if (selectedOrder) {
        try {
          const isComposite = String(selectedOrder).includes(":");
          const url = isComposite
            ? `${API}/messages/case/${encodeURIComponent(selectedOrder)}`
            : `${API}/messages/order/${selectedOrder}`;
          const r = await axios.get(url);
          setThreadDetails({ orderId: selectedOrder, msgs: r.data?.items || [], loading: false });
        } catch (_) { /* best-effort refresh */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setHelpfulModalBusy(false); }
  };

  const handleSaveTemplate = async () => {
    if (!tplEdit?.name?.trim()) { toast.error("Adaugă un nume"); return; }
    try {
      if (tplEdit.id) {
        await axios.put(`${API}/messages/templates/${tplEdit.id}`, { name: tplEdit.name, body: tplEdit.body || "" });
      } else {
        await axios.post(`${API}/messages/templates`, { name: tplEdit.name, body: tplEdit.body || "" });
      }
      setTplEdit(null);
      await loadTemplates();
      toast.success("Template salvat");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/messages/templates/${id}`);
      await loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="messages-page" className="flex h-[calc(100vh-3.5rem)] flex-col bg-zinc-950 text-zinc-100 md:h-screen">
      {/* Header / filters */}
      <div className="border-b border-zinc-800 bg-zinc-950/95 px-6 py-3">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 p-2 shadow-lg shadow-sky-500/20">
            <Inbox className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-[160px] flex-1">
            <h1 className="text-base font-semibold text-zinc-50">Mesaje eMAG</h1>
            <p className="text-[11px] text-zinc-500">Inbox unificat pentru toate conturile eMAG</p>
          </div>
          <button
            data-testid="messages-new-thread-btn"
            onClick={() => setNewThreadOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-medium text-orange-200 hover:bg-orange-500/20"
          >
            <Plus className="h-3.5 w-3.5" /> Mesaj nou
          </button>
          <button
            data-testid="messages-templates-btn"
            onClick={() => setTemplatesOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:border-violet-500/40 hover:text-violet-200"
          >
            <FileText className="h-3.5 w-3.5" /> Templates
          </button>
          <button
            data-testid="messages-sync-btn"
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Se sincronizează…" : "Sincronizează acum"}
          </button>
          <button
            data-testid="messages-backfill-btn"
            onClick={async () => {
              if (!window.confirm("Backfill ultimele 10 zile? Aduce TOATE mesajele/reclamațiile, inclusiv cele Rezolvate.")) return;
              try {
                const r = await axios.post(`${API}/messages/backfill?days=10`);
                toast.success(`Backfill OK — ${r.data.new} noi din ${r.data.fetched} găsite`);
                load();
              } catch (e) {
                toast.error("Backfill eșuat: " + (e.response?.data?.detail || e.message));
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/20"
            title="Import unic — ultimele 10 zile, inclusiv cazuri Rezolvate"
          >
            <Inbox className="h-3.5 w-3.5" /> Backfill 10z
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
            <Filter className="h-3.5 w-3.5" /> Filtre:
          </div>
          <select
            data-testid="filter-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 focus:border-sky-500/50 focus:outline-none"
          >
            {PLATFORM_OPTS.map((o) => <option key={o.value} value={o.value} className="bg-zinc-900">{o.label}</option>)}
          </select>
          <select
            data-testid="filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 focus:border-sky-500/50 focus:outline-none"
          >
            {STATUS_OPTS.map((o) => <option key={o.value} value={o.value} className="bg-zinc-900">{o.label}</option>)}
          </select>
          <select
            data-testid="filter-case-type"
            value={caseType}
            onChange={(e) => setCaseType(e.target.value)}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 focus:border-sky-500/50 focus:outline-none"
          >
            <option value="all">Toate tipurile</option>
            <option value="thread">Mesaje</option>
            <option value="claim">Reclamații</option>
            <option value="thread-am">Thread AM</option>
          </select>
          <input
            data-testid="filter-message-id"
            placeholder="ID eMAG"
            inputMode="numeric"
            value={messageIdFilter}
            onChange={(e) => setMessageIdFilter(e.target.value.replace(/[^0-9]/g, ""))}
            className="w-28 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-sky-500/50 focus:outline-none"
          />
          <input
            data-testid="filter-order-id"
            placeholder="ID comandă"
            value={orderIdFilter}
            onChange={(e) => setOrderIdFilter(e.target.value.replace(/[^0-9]/g, ""))}
            className="w-28 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-sky-500/50 focus:outline-none"
          />
          <div className="relative flex-1 min-w-[180px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="filter-search"
              placeholder="Caută în text sau autor…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 pl-7 pr-3 py-1 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-sky-500/50 focus:outline-none"
            />
          </div>
          {(platform !== "all" || status !== "all" || caseType !== "all" || q || messageIdFilter || orderIdFilter) && (
            <button
              data-testid="clear-filters"
              onClick={() => { setPlatform("all"); setStatus("all"); setCaseType("all"); setQ(""); setMessageIdFilter(""); setOrderIdFilter(""); }}
              className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-400 hover:text-zinc-100"
            >
              <X className="h-3 w-3" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* eMAG Claims-Messaging v4.5.1 status banner */}
      <div className="border-b border-emerald-500/30 bg-emerald-500/5 px-6 py-2">
        <p className="text-[11px] text-emerald-200/90">
          <span className="font-semibold">✅ Activ:</span> conectat la eMAG Claims-Messaging API v4.5.1. Se sincronizează automat mesajele (thread), reclamațiile (claim) și thread-urile AM. Pentru răspuns, deschide firul și trimite din platformă.
        </p>
      </div>

      {/* Body: conversations list + thread view */}
      <div className="flex flex-1 overflow-hidden">
        <aside data-testid="conversations-list" className={`${activeThread ? "w-full lg:max-w-md" : "w-full"} flex-shrink-0 overflow-y-auto border-r border-zinc-800 bg-zinc-950`}>
          {loading && <p className="px-4 py-3 text-xs text-zinc-500">Se încarcă…</p>}
          {!loading && groups.length === 0 && (
            <div className="px-6 py-12 text-center">
              <MessageSquare className="mx-auto h-8 w-8 text-zinc-700" />
              <p className="mt-3 text-xs text-zinc-500">Nicio conversație.</p>
              <p className="mt-1 text-[10px] text-zinc-600">Apasă „Sincronizează acum” pentru a aduce mesajele din eMAG.</p>
            </div>
          )}
          {!activeThread && groups.length > 0 && (
            <div className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-900/80 backdrop-blur grid grid-cols-[minmax(120px,1fr)_minmax(80px,110px)_minmax(220px,2fr)_minmax(140px,1fr)_100px_100px_100px] gap-2 px-4 py-2 text-[9px] font-semibold uppercase tracking-wider text-zinc-500">
              <span>Seller</span>
              <span>Data / Ora</span>
              <span>Titlu</span>
              <span>Subcategorie</span>
              <span>Sursă</span>
              <span>Status</span>
              <span>Max răsp.</span>
            </div>
          )}
          {groups.map((g) => {
            const isActive = activeThread?.orderId === g.orderId;
            const latest = g.last || {};
            const st = latest.status || "";
            const statusMap = { "Nou": "bg-zinc-700/60 text-zinc-200 border-zinc-500",
                                "In asteptare": "bg-sky-500/20 text-sky-200 border-sky-500/40",
                                "Raspuns": "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
                                "Rezolvat": "bg-zinc-800 text-zinc-400 border-zinc-700",
                                "Irelevant": "bg-zinc-800 text-zinc-500 border-zinc-700" };
            const sellerLabel = (PLATFORM_OPTS.find((p) => p.value === g.integration) || {}).label || g.integration;
            const maxResp = latest.max_response_date ? String(latest.max_response_date).slice(0, 10) : "";
            // Title: for orders → "ORDER ID: #123", else full subject (or fallback).
            const titleLabel = threadDisplayLabel(g);
            // Subcategory: prefer topic_name (already resolved server-side).
            // Fall back to `type_name` if only Tip is known.
            const subcat = g.subcategory || latest.subcategory || g.typeName || latest.type_name || "—";
            const source = SOURCE_LABEL[g.initiatedBy] || SOURCE_LABEL[latest.initiated_by] || (g.caseType === "claim" ? "Client" : "—");

            if (activeThread) {
              // Compact sidebar row when thread is open (original style)
              return (
                <button
                  key={g.orderId}
                  data-testid={`conv-${g.orderId}`}
                  onClick={() => openThread(g.orderId)}
                  className={`flex w-full items-start gap-3 border-b border-zinc-900 px-4 py-3 text-left transition ${isActive ? "lg:bg-sky-500/10" : "hover:bg-zinc-900/60"}`}
                >
                  <div className={`mt-0.5 h-2 w-2 flex-shrink-0 rounded-full ${g.unread > 0 ? "bg-sky-400" : "bg-transparent"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <span className="font-mono text-xs font-medium text-zinc-100 truncate">{titleLabel}</span>
                      <span className="text-[10px] text-zinc-500 flex-shrink-0">{fmtDate(latest.created_at)}</span>
                    </div>
                    {/* eMAG ID sub titlu (user cerinta Oct 2026). Daca nu avem
                        caseId, cadem inapoi la subcategorie. */}
                    {g.caseId ? (
                      <p className="mb-1 line-clamp-1 font-mono text-[11px] text-sky-400">eMAG #{g.caseId}</p>
                    ) : (
                      <p className="mb-1 line-clamp-1 text-[11px] text-zinc-400">{subcat}</p>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] uppercase tracking-wider text-zinc-600 truncate">{sellerLabel} · {source}</span>
                      {st && <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase flex-shrink-0 ${statusMap[st] || "border-zinc-700 bg-zinc-800 text-zinc-400"}`}>{st}</span>}
                    </div>
                  </div>
                </button>
              );
            }
            // Full inbox row layout when no thread selected (mail-style)
            return (
              <button
                key={g.orderId}
                data-testid={`conv-${g.orderId}`}
                onClick={() => openThread(g.orderId)}
                className="grid w-full grid-cols-[minmax(120px,1fr)_minmax(80px,110px)_minmax(220px,2fr)_minmax(140px,1fr)_100px_100px_100px] items-center gap-2 border-b border-zinc-900 px-4 py-2.5 text-left transition hover:bg-zinc-900/60"
              >
                <div className="min-w-0">
                  <div className="text-[11px] font-medium text-zinc-100 truncate">{sellerLabel}</div>
                  <div className="text-[9px] text-zinc-500 truncate flex items-center gap-1">
                    <span>{(latest.case_type || "thread").toUpperCase()} #{latest.id ?? "?"}</span>
                    {(() => {
                      const url = emagMessageUrl(g.integration, latest.id);
                      if (!url) return null;
                      return (
                        <span
                          role="button"
                          tabIndex={0}
                          data-testid={`row-emag-link-${g.orderId}`}
                          title={latest.id ? `Deschide thread #${latest.id} pe eMAG` : "Deschide Seller Center eMAG"}
                          onClick={(e) => { e.stopPropagation(); window.open(url, "_blank", "noopener,noreferrer"); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.open(url, "_blank", "noopener,noreferrer"); } }}
                          className="inline-flex h-4 w-4 items-center justify-center rounded text-sky-400 hover:bg-sky-500/20 hover:text-sky-200"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] text-zinc-300">{fmtDate(latest.created_at)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-medium text-zinc-100 truncate" title={titleLabel}>{titleLabel}</div>
                  {/* eMAG ID sub titlu (user cerinta Oct 2026) */}
                  {g.caseId && (
                    <div className="font-mono text-[10px] text-sky-400 truncate">eMAG #{g.caseId}</div>
                  )}
                  {latest.text && <div className="text-[10px] text-zinc-500 line-clamp-1">{latest.text}</div>}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] text-zinc-300 truncate" title={subcat}>{subcat}</div>
                  {(latest.type_name || g.typeName) && (
                    <div className="text-[9px] text-zinc-600 truncate">{latest.type_name || g.typeName}</div>
                  )}
                </div>
                <div className="min-w-0">
                  <span className="rounded-md border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-[10px] text-zinc-300">{source}</span>
                </div>
                <div>
                  {st ? (
                    <span data-testid={`status-${g.orderId}`} className={`rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${statusMap[st] || "border-zinc-700 bg-zinc-800 text-zinc-400"}`}>{st}</span>
                  ) : "—"}
                  {g.unread > 0 && (
                    <span className="ml-1 inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-sky-500 px-1 text-[8px] font-bold text-zinc-950">{g.unread}</span>
                  )}
                </div>
                <div className="text-[10px] text-zinc-400">{maxResp || "—"}</div>
              </button>
            );
          })}
        </aside>

        {(() => { const pane = (
        <main
          data-testid="thread-pane"
          style={mobileOpen ? { paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" } : undefined}
          className={`${mobileOpen ? "fixed inset-0 z-[60] flex" : "hidden"} flex-1 flex-col overflow-hidden bg-zinc-950 lg:static lg:z-auto lg:flex lg:!p-0`}
        >
          {!activeThread ? (
            <div className="flex flex-1 items-center justify-center text-center">
              <div>
                <MessageSquare className="mx-auto h-12 w-12 text-zinc-800" />
                <p className="mt-3 text-sm text-zinc-500">Selectează o conversație din stânga.</p>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                data-testid="thread-mobile-back-btn"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 border-b border-zinc-800 bg-zinc-900 px-4 py-3 text-sm font-medium text-sky-300 lg:hidden"
              >
                <ArrowLeft className="h-4 w-4" /> Înapoi la mesaje
              </button>
              <div className="flex flex-col gap-3 border-b border-zinc-800 bg-zinc-900/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p data-testid="thread-title" className="font-mono text-sm font-semibold text-zinc-100">
                      {activeThread.hasOrder
                        ? `ORDER ID: #${activeThread.orderId}`
                        : (activeThread.subject
                            ? activeThread.subject
                            : threadDisplayLabel(activeThread))}
                    </p>
                    {(() => {
                      // Aceeași sursă ca sidebar-ul (lista) — nu mai pot diverge.
                      const st = activeThread.last?.status || "";
                      const map = { "Nou": "bg-zinc-700/60 text-zinc-200 border-zinc-500",
                                    "In asteptare": "bg-sky-500/20 text-sky-200 border-sky-500/40",
                                    "Raspuns": "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
                                    "Rezolvat": "bg-zinc-800 text-zinc-400 border-zinc-700",
                                    "Irelevant": "bg-zinc-800 text-zinc-500 border-zinc-700" };
                      if (!st) return null;
                      return <span data-testid="thread-status-badge" className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${map[st] || "border-zinc-700 bg-zinc-800 text-zinc-400"}`}>{st}</span>;
                    })()}
                      {/* User cerință Feb 2026: marchează manual statusul —
                          override peste ce spune eMAG (care greșește). */}
                      <div className="relative inline-block" data-testid="mark-status-wrapper">
                        <select
                          data-testid="mark-status-select"
                          className="inline-flex items-center gap-1 rounded-lg border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-200 hover:bg-violet-500/20 cursor-pointer"
                          value=""
                          title="Marchează manual statusul (override peste eMAG)"
                          onChange={async (e) => {
                            const st = e.target.value;
                            e.target.value = "";
                            if (!st || !selectedOrder) return;
                            // RCA Feb 2026: pentru mesajele deschise via
                            // deep-link (URL query ?order_id=X), `selectedOrder`
                            // este ID-ul numeric al comenzii, NU _composite_id.
                            // Backend-ul `/mark-status/{id}` caută pe
                            // `_composite_id` → 404 „Mesaj negăsit". Rezolvăm
                            // trimițând compozitul parent al ultimului mesaj
                            // din thread (strippat de sufixul de comment).
                            const lastMsg = activeThread?.msgs?.[activeThread.msgs.length - 1];
                            const parentComposite = (lastMsg?._composite_id || "")
                              .replace(/:c\d+$/, "");
                            const targetId = parentComposite || selectedOrder;
                            try {
                              await axios.post(
                                `${API}/messages/${encodeURIComponent(targetId)}/mark-status`,
                                { status: st === "clear" ? "" : st }
                              );
                              toast.success(st === "clear" ? "Override șters" : `Marcat: ${st}`);
                              await load();
                            } catch (err) {
                              toast.error("Nu s-a putut marca: " + (err.response?.data?.detail || err.message));
                            }
                          }}
                        >
                          <option value="" disabled>Marchează ca…</option>
                          <option value="Nou">Nou</option>
                          <option value="In asteptare">În așteptare</option>
                          <option value="Raspuns">Răspuns</option>
                          <option value="Rezolvat">Rezolvat</option>
                          <option value="clear">↺ Șterge override</option>
                        </select>
                      </div>
                    <button
                      type="button"
                      data-testid="insert-warranty-link-btn"
                      onClick={() => setWarrantyAsk(true)}
                      disabled={replying}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                      title="Trimite clientului link-ul formularului de garanție"
                    >
                      <Shield className="h-3 w-3" />
                      Link garanție
                    </button>
                    <AlertDialog open={warrantyAsk} onOpenChange={setWarrantyAsk}>
                      <AlertDialogContent data-testid="warranty-confirm-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Trimiți link-ul de garanție?</AlertDialogTitle>
                          <AlertDialogDescription>Ești sigur că vrei să trimiți clientului link-ul către formularul de garanție? Mesajul pleacă imediat pe eMAG.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="warranty-confirm-no">Nu</AlertDialogCancel>
                          <AlertDialogAction data-testid="warranty-confirm-yes" onClick={() => handleReply(`Bună ziua, vă rugăm completați formularul de garanție aici: ${window.location.origin}/warranty-form`)}>Da, trimite</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    {(() => {
                      const latest = activeThread.msgs[activeThread.msgs.length - 1] || {};
                      if (latest.severity && latest.severity !== "Low") {
                        const sev = latest.severity;
                        const cls = sev === "Critical" ? "border-rose-500/50 bg-rose-500/15 text-rose-200" : "border-orange-500/50 bg-orange-500/15 text-orange-200";
                        return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>{sev}</span>;
                      }
                      return null;
                    })()}
                    {(() => {
                      // Direct deep-link into the eMAG Seller Center for this
                      // thread (user request 2026-08-18). Removed 2026-02-23
                      // — the blue button didn't resolve to a working URL for
                      // most case types. The orange „open-emag-claim" below
                      // stays as the single source of truth.
                      return null;
                    })()}
                  </div>
                  {/* eMAG ID — sub titlu, nu la final (user cerinta Oct 2026).
                      Afisat ca prim subtitlu, cu font mono pentru lizibilitate. */}
                  {activeThread.caseId && (
                    <p className="mt-1 font-mono text-[11px] text-sky-400" data-testid="thread-emag-id">
                      eMAG #{activeThread.caseId}
                    </p>
                  )}
                  {/* Full subject line for order-linked threads (extra info) */}
                  {activeThread.hasOrder && activeThread.subject && (
                    <p className="mt-1 text-[11px] text-zinc-300 truncate" title={activeThread.subject}>
                      Titlu: {activeThread.subject}
                    </p>
                  )}
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-zinc-500">
                    {(PLATFORM_OPTS.find((p) => p.value === activeThread.integration) || {}).label || activeThread.integration}
                    {" · "}{activeThread.msgs.length} mesaje
                    {(() => {
                      const latest = activeThread.msgs[activeThread.msgs.length - 1] || {};
                      const parts = [];
                      if (latest.type_name) parts.push(`Tip: ${latest.type_name}`);
                      if (latest.subcategory) parts.push(`Subcategorie: ${latest.subcategory}`);
                      const src = SOURCE_LABEL[latest.initiated_by] || (activeThread.caseType === "claim" ? "Client" : "");
                      if (src) parts.push(`Sursă: ${src}`);
                      if (latest.max_response_date) parts.push(`răspuns până: ${String(latest.max_response_date).slice(0, 10)}`);
                      return parts.length ? " · " + parts.join(" · ") : "";
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const latest = activeThread.msgs[activeThread.msgs.length - 1] || {};
                    if (latest.status === "Rezolvat" && !latest.confirmed_closed) {
                      return (
                        <button
                          data-testid="confirm-close-btn"
                          onClick={async () => {
                            if (!window.confirm("Confirmi închiderea acestui caz?")) return;
                            try {
                              await axios.post(`${API}/messages/${latest._composite_id}/confirm-close`);
                              toast.success("Caz închis");
                              load();
                            } catch (e) {
                              toast.error("Eroare: " + (e.response?.data?.detail || e.message));
                            }
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
                          title="eMAG a marcat cazul Rezolvat — confirmă OK"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Confirmă închiderea
                        </button>
                      );
                    }
                    return null;
                  })()}
                  {/* Direct link to eMAG claim page. Uses parent_case_id from the
                      latest message (each claim has its own id — 17498471 style).
                      Falls back to activeThread.caseId (from group metadata). */}
                  {(() => {
                    const latest = activeThread.msgs[activeThread.msgs.length - 1] || {};
                    // Numeric claim id (e.g. 17498471) — populated by
                    // backend `_case_id`. Fallback: try to extract from
                    // `_parent_case_id` (format `emag_x:claim:12345`).
                    let claimId = latest._case_id;
                    if (!claimId && latest._parent_case_id) {
                      const m = String(latest._parent_case_id).match(/:(\d+)$/);
                      if (m) claimId = m[1];
                    }
                    if (!claimId && activeThread.caseId) claimId = activeThread.caseId;
                    if (!claimId) return null;
                    return (
                      <a
                        href={`https://marketplace.emag.ro/claims/edit/${claimId}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="open-emag-claim"
                        className="inline-flex items-center gap-1 rounded-md border border-orange-500/40 bg-orange-500/10 px-2.5 py-1.5 text-[11px] font-medium text-orange-200 hover:bg-orange-500/20"
                        title={`Deschide claim #${claimId} pe marketplace.emag.ro`}
                      >
                        <ExternalLink className="h-3 w-3" /> Deschide pe eMAG
                      </a>
                    );
                  })()}
                  {activeThread.hasOrder && (
                    <a
                      href={`/orders?order_id=${encodeURIComponent(activeThread.orderId)}&days=365`}
                      data-testid="open-order-link"
                      className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-[11px] text-zinc-300 hover:border-orange-500/40 hover:text-orange-200"
                    >
                      <ExternalLink className="h-3 w-3" /> Deschide comanda
                    </a>
                  )}
                </div>
              </div>

              <div data-testid="thread-messages" className="flex flex-1 flex-col gap-3 overflow-y-auto bg-gradient-to-b from-zinc-950 to-zinc-950 p-5">
                {activeThread.loading && (
                  <div className="flex items-center justify-center py-6 text-xs text-zinc-500" data-testid="thread-loading">
                    Se încarcă conversația din eMAG...
                  </div>
                )}
                {activeThread.msgs.map((m, i) => (
                  <div key={m._composite_id || i} className={`flex ${m.from_buyer ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${m.from_buyer ? "bg-zinc-900 text-zinc-100 ring-1 ring-zinc-800" : "bg-sky-500/15 text-sky-100 ring-1 ring-sky-500/30"}`}>
                      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-70">
                        <span>{m.from_buyer ? (m.author || "Client") : "Tu"}</span>
                        <span>·</span>
                        <span>{fmtDate(m.created_at)}</span>
                        {m.is_unread && (
                          <span className="ml-1 rounded-full bg-sky-500/30 px-1.5 py-0.5 text-[9px] font-semibold text-sky-100">NOU</span>
                        )}
                      </div>
                      <p className="whitespace-pre-wrap break-words leading-relaxed">{decodeEntities(m.text) || "—"}</p>
                      {(m.attachments || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5 border-t border-white/10 pt-2">
                          {m.attachments.map((att) => (
                            <button
                              key={att.id}
                              data-testid={`att-${att.id}`}
                              onClick={() => downloadCaseAttachment(
                                m._parent_case_id || activeThread.orderId,
                                m.id, att.id, att.name || att.fileName || `att-${att.id}`,
                              )}
                              title="Descarcă atașament"
                              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-black/20 px-2 py-1 text-[11px] hover:bg-black/40"
                            >
                              <Paperclip className="h-3 w-3" />
                              <span className="max-w-[220px] truncate">{att.name || att.fileName || `Atașament #${att.id}`}</span>
                              <Download className="h-3 w-3 opacity-60" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-zinc-800 bg-zinc-950 p-4">
                {helpfulPrompt ? (
                  /* eMAG "Was this helpful?" prompt — user request 2026-02.
                     When active, disable free-text; user picks DA/NU and Submit.
                     DA → sends "DA" (case auto-resolves via eMAG).
                     NU → sends "NU" (case continues, user can then reply). */
                  <div data-testid="helpful-prompt" className="space-y-3">
                    <p className="text-sm text-zinc-200 font-medium">Răspunsul primit te-a ajutat să rezolvi situația?</p>
                    <div className="flex items-center gap-4">
                      <button
                        data-testid="helpful-yes"
                        onClick={() => handleHelpful(true)}
                        disabled={helpfulModalBusy}
                        className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/20 px-4 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
                      >
                        <CheckCircle2 className="h-4 w-4" /> DA (Rezolvat)
                      </button>
                      <button
                        data-testid="helpful-no"
                        onClick={() => handleHelpful(false)}
                        disabled={helpfulModalBusy}
                        className="inline-flex items-center gap-2 rounded-lg border border-rose-500/50 bg-rose-500/20 px-4 py-2 text-sm font-medium text-rose-100 hover:bg-rose-500/30 disabled:opacity-40"
                      >
                        <X className="h-4 w-4" /> NU (continuă)
                      </button>
                    </div>
                    <p className="text-[10px] text-zinc-500">DA închide cazul cu răspunsul automat eMAG. NU trimite &quot;NU&quot; ca răspuns și poți continua conversația.</p>
                  </div>
                ) : (
                  <>
                    {templates.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {templates.map((t) => (
                          <button
                            key={t.id}
                            data-testid={`tpl-quick-${t.id}`}
                            onClick={() => setReplyText((prev) => prev ? `${prev}\n${t.body}` : t.body)}
                            className="rounded-full border border-zinc-800 bg-zinc-900 px-2.5 py-1 text-[10px] text-zinc-300 hover:border-violet-500/40 hover:text-violet-200"
                            title={t.body}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <div className="relative flex-1">
                      <textarea
                        data-testid="reply-textarea"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Scrie răspunsul tău…"
                        rows={2}
                        className="w-full resize-none rounded-lg border border-zinc-800 bg-zinc-900 py-2 pl-3 pr-10 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-sky-500/50 focus:outline-none"
                      />
                      <label
                        data-testid="attach-btn"
                        className="absolute bottom-2 right-2 cursor-pointer rounded-md p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-orange-200"
                        title="Adaugă atașamente"
                      >
                        <Paperclip className="h-4 w-4" />
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => setReplyFiles(Array.from(e.target.files || []))}
                        />
                      </label>
                      </div>
                      <button
                        data-testid="send-reply-btn"
                        onClick={() => handleReply()}
                        disabled={replying || (!replyText.trim() && replyFiles.length === 0)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/50 bg-sky-500/20 px-4 py-2 text-xs font-medium text-sky-100 transition hover:bg-sky-500/30 disabled:opacity-40"
                      >
                        <Send className="h-3.5 w-3.5" /> {replying ? "Se trimite…" : "Trimite"}
                      </button>
                    </div>
                    {replyFiles.length > 0 && (
                      <div data-testid="reply-file-list" className="mt-2 flex flex-wrap gap-1.5">
                        {replyFiles.map((f, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300">
                            <Paperclip className="h-3 w-3" />
                            <span className="max-w-[180px] truncate">{f.name}</span>
                            <button
                              onClick={() => setReplyFiles((prev) => prev.filter((_, j) => j !== i))}
                              className="text-zinc-500 hover:text-rose-300"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </main>
        ); return mobileOpen && window.matchMedia("(max-width: 1023px)").matches ? createPortal(pane, document.body) : pane; })()}
      </div>

      {/* Templates modal */}
      {templatesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="templates-modal">
          <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-violet-300" /><h2 className="text-sm font-semibold text-zinc-100">Template-uri răspuns</h2></div>
              <button onClick={() => { setTemplatesOpen(false); setTplEdit(null); }} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {tplEdit ? (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Nume</label>
                    <input
                      data-testid="tpl-name-input"
                      value={tplEdit.name}
                      onChange={(e) => setTplEdit({ ...tplEdit, name: e.target.value })}
                      placeholder="ex: Confirmare comandă"
                      className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 focus:border-violet-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Conținut</label>
                    <textarea
                      data-testid="tpl-body-input"
                      value={tplEdit.body}
                      onChange={(e) => setTplEdit({ ...tplEdit, body: e.target.value })}
                      rows={6}
                      placeholder="Conținutul mesajului…"
                      className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-violet-500/50 focus:outline-none"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <button onClick={() => setTplEdit(null)} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800">Renunță</button>
                    <button data-testid="tpl-save-btn" onClick={handleSaveTemplate} className="rounded-md border border-violet-500/50 bg-violet-500/20 px-3 py-1.5 text-xs font-medium text-violet-100 hover:bg-violet-500/30">Salvează</button>
                  </div>
                </div>
              ) : (
                <>
                  <button data-testid="tpl-new-btn" onClick={() => setTplEdit({ name: "", body: "" })} className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-violet-500/50 bg-violet-500/15 px-3 py-1.5 text-xs font-medium text-violet-100 hover:bg-violet-500/25">
                    <Plus className="h-3.5 w-3.5" /> Adaugă template
                  </button>
                  {templates.length === 0 ? (
                    <p className="text-xs text-zinc-500">Niciun template salvat încă.</p>
                  ) : (
                    <ul className="divide-y divide-zinc-900">
                      {templates.map((t) => (
                        <li key={t.id} className="flex items-start justify-between gap-3 py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-zinc-100">{t.name}</p>
                            <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-500">{t.body}</p>
                          </div>
                          <div className="flex gap-1">
                            <button data-testid={`tpl-edit-${t.id}`} onClick={() => setTplEdit(t)} className="rounded-md border border-zinc-800 bg-zinc-900 p-1.5 text-zinc-400 hover:text-violet-300"><Pencil className="h-3 w-3" /></button>
                            <button data-testid={`tpl-delete-${t.id}`} onClick={() => handleDeleteTemplate(t.id)} className="rounded-md border border-zinc-800 bg-zinc-900 p-1.5 text-zinc-400 hover:text-rose-300"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {newThreadOpen && (
        <NewThreadModal
          onClose={() => setNewThreadOpen(false)}
          onCreated={() => { setNewThreadOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// eMAG Seller Center URLs for the "fallback" path when API create isn't
// available. RO uses "Contactează Mira" (their internal AI). BG/HU point
// to the marketplace directly.
const SELLER_CENTER_URL = {
  emag_ro_fbe: "https://marketplace.emag.ro/",
  emag_ro_bau: "https://marketplace.emag.ro/",
  emag_bg_fbe: "https://marketplace.emag.bg/",
  emag_bg_bau: "https://marketplace.emag.bg/",
  emag_hu_fbe: "https://marketplace.emag.hu/",
  emag_hu_bau: "https://marketplace.emag.hu/",
};

// Direct link into a specific message thread on eMAG's Seller Center.
// User request 2026-08-18: every message row should have a clickable
// deep-link (falls back to the seller center home if the id is missing).
// eMAG doesn't publish an official per-message URL scheme; the pattern
// below matches what the Seller Center UI uses internally. If eMAG ever
// breaks the pattern, users land on the marketplace home instead of a
// 404 (fallback in the ternary).
function emagMessageUrl(integrationKey, threadId) {
  const base = SELLER_CENTER_URL[integrationKey];
  if (!base) return null;
  if (!threadId) return base;
  // Common pattern across all eMAG marketplaces: /messages/thread/{id}
  // (empirical — verified by opening from Seller Center → Mesaje → click).
  return `${base}messages/thread/${threadId}`;
}

function NewThreadModal({ onClose, onCreated }) {
  const [accountKey, setAccountKey] = useState("emag_hu_fbe");
  const [typesTopics, setTypesTopics] = useState(null);
  const [typeId, setTypeId] = useState("");
  const [topicId, setTopicId] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [orderId, setOrderId] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingTT, setLoadingTT] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);

  const platform = accountKey.split("_")[1]; // ro / bg / hu
  const isRO = platform === "ro";

  useEffect(() => {
    if (isRO) return;
    setLoadingTT(true);
    setApiFailed(false);
    axios.get(`${API}/messages/types-topics/${accountKey}`)
      .then((r) => setTypesTopics(r.data))
      .catch(() => setTypesTopics(null))
      .finally(() => setLoadingTT(false));
    // eslint-disable-next-line
  }, [accountKey]);

  const typesArr = typesTopics?.raw?.types || typesTopics?.raw?.caseTypes || [];
  const selectedType = typesArr.find((t) => String(t.id || t.typeId) === String(typeId));
  const topicsArr = selectedType?.topics || [];

  const handleSubmit = async () => {
    if (isRO) {
      window.open(SELLER_CENTER_URL[accountKey], "_blank");
      onClose();
      return;
    }
    if (!typeId || !topicId || !subject.trim() || !content.trim()) {
      toast.error("Completează Tip, Subcategorie, Titlu și Text.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("account_key", accountKey);
      fd.append("case_type", "thread");
      fd.append("type_id", String(typeId));
      fd.append("topic_id", String(topicId));
      fd.append("subject", subject);
      fd.append("content", content);
      if (orderId.trim()) fd.append("order_id", orderId.trim());
      for (const f of files) fd.append("files", f);
      const r = await axios.post(`${API}/messages/create`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Mesaj creat pe eMAG (case #${r.data?.case_id || "?"}).`);
      onCreated();
    } catch (e) {
      const status = e.response?.status;
      if (status === 501) {
        setApiFailed(true);
        toast.error("eMAG nu suportă crearea prin API. Folosește link-ul de mai jos.");
      } else {
        toast.error(e.response?.data?.detail || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="new-thread-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-orange-300" />
            <h2 className="text-sm font-semibold text-zinc-100">
              {isRO ? "Contactează Mira (eMAG RO)" : "Deschide mesaj nou eMAG"}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Cont eMAG</label>
            <select
              data-testid="new-thread-account"
              value={accountKey}
              onChange={(e) => setAccountKey(e.target.value)}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
            >
              <option value="emag_ro_fbe">eMAG RO FBE (Mira)</option>
              <option value="emag_ro_bau">eMAG RO BAU (Mira)</option>
              <option value="emag_bg_fbe">eMAG BG FBE</option>
              <option value="emag_bg_bau">eMAG BG BAU</option>
              <option value="emag_hu_fbe">eMAG HU FBE</option>
              <option value="emag_hu_bau">eMAG HU BAU</option>
            </select>
          </div>

          {isRO ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
              <p className="mb-2 font-medium">Pentru eMAG RO folosește Mira (AI-ul lor)</p>
              <p className="text-[12px] text-amber-200/90">
                eMAG RO Marketplace are asistentul AI &quot;Mira&quot; pentru mesaje către suport.
                Apasă butonul de mai jos pentru a deschide eMAG Seller Center într-un tab nou
                și scrie prin Mira direct pe platforma lor.
              </p>
              <a
                data-testid="mira-link"
                href={SELLER_CENTER_URL[accountKey]}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-100 hover:bg-amber-500/25"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Contactează Mira în eMAG
              </a>
            </div>
          ) : (
            <>
              {loadingTT ? (
                <p className="text-xs text-zinc-500">Se încarcă tipurile/subcategoriile de la eMAG…</p>
              ) : !typesTopics ? (
                <p className="text-xs text-rose-300">Nu s-au putut încărca tipurile. Verifică conexiunea.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Tip *</label>
                      <select
                        data-testid="new-thread-type"
                        value={typeId}
                        onChange={(e) => { setTypeId(e.target.value); setTopicId(""); }}
                        className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                      >
                        <option value="">— alege —</option>
                        {typesArr.map((t) => (
                          <option key={t.id || t.typeId} value={t.id || t.typeId}>
                            {t.name || t.label || `Type ${t.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Subcategorie *</label>
                      <select
                        data-testid="new-thread-topic"
                        value={topicId}
                        onChange={(e) => setTopicId(e.target.value)}
                        disabled={!typeId}
                        className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none disabled:opacity-50"
                      >
                        <option value="">— alege —</option>
                        {topicsArr.map((tp) => (
                          <option key={tp.id || tp.topicId} value={tp.id || tp.topicId}>
                            {tp.name || tp.label || `Topic ${tp.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Titlu *</label>
                    <input
                      data-testid="new-thread-subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Titlu mesaj"
                      className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Text *</label>
                    <textarea
                      data-testid="new-thread-content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows={5}
                      placeholder="Corpul mesajului…"
                      className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">ID comandă (opțional)</label>
                      <input
                        data-testid="new-thread-order"
                        value={orderId}
                        onChange={(e) => setOrderId(e.target.value.replace(/[^0-9]/g, ""))}
                        placeholder="ex: 499244876"
                        className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Atașamente</label>
                      <input
                        data-testid="new-thread-files"
                        type="file"
                        multiple
                        onChange={(e) => setFiles(Array.from(e.target.files || []))}
                        className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-[11px] text-zinc-300 file:mr-2 file:rounded file:border-0 file:bg-zinc-800 file:px-2 file:py-0.5 file:text-[11px] file:text-zinc-300"
                      />
                      {files.length > 0 && (
                        <p className="mt-1 text-[10px] text-zinc-500">{files.length} fișier(e) selectate</p>
                      )}
                    </div>
                  </div>
                  {apiFailed && (
                    <div className="rounded-md border border-rose-500/40 bg-rose-500/5 p-3">
                      <p className="text-[11px] text-rose-200">
                        API-ul eMAG nu suportă crearea de thread nou pentru acest cont.
                        Deschide Seller Center-ul manual:
                      </p>
                      <a
                        href={SELLER_CENTER_URL[accountKey]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-rose-100 underline"
                      >
                        <ExternalLink className="h-3 w-3" /> {SELLER_CENTER_URL[accountKey]}
                      </a>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-4 py-3">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800">
            Renunță
          </button>
          <button
            data-testid="new-thread-submit"
            onClick={handleSubmit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/50 bg-orange-500/20 px-4 py-1.5 text-xs font-medium text-orange-100 hover:bg-orange-500/30 disabled:opacity-40"
          >
            {isRO ? <ExternalLink className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            {isRO ? "Deschide Mira" : (busy ? "Se trimite…" : "Trimite pe eMAG")}
          </button>
        </div>
      </div>
    </div>
  );
}
